import { frameId, type AssetManifest } from "../assets/manifest.ts";
import { EMPTY_TILE } from "../document/packed-tile.ts";
import { COLLISION_CELLS_PER_TILE, type EntityInstance, type JsonValue, type MapDocument } from "../document/types.ts";
import { cellAreasOf, cellBox, thingAnchor } from "./cell-fields.ts";
import type { EntityTypeDefinition } from "./schema.ts";
import { visibleChunks, type ChunkRange, type ViewRect } from "./view-chunks.ts";

/**
 * The density meter's count (ADR-088 decisions 2, 3 and 5): what the game loads around a point, read from the
 * document. The view is the game's registered camera view centred on the point and kept inside the map as the
 * game's bounded camera keeps it; the range is `visibleChunks` of that view, the chunks the game's painter keeps.
 * Over it: the props by the chunk holding their point (ADR-016 decision 6), the ones whose frame loops an
 * animation, the atlas pages they draw from, each tile layer's chunks holding a painted cell (one chunk texture
 * each, ADR-019), and each group the game registers. Each count is graded against its budget. Pure: plain data in
 * and out; it never blocks anything, it only reports.
 */

/**
 * A point in world pixels.
 */
export interface DensityPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * A metric's thresholds: green under `amber`, amber from `amber` to under `red`, red from `red`.
 */
export interface DensityBudget {
  readonly amber: number;
  readonly red: number;
}

/**
 * A reading's grade.
 */
export type DensityGrade = "green" | "amber" | "red";

/**
 * The metrics Daedalus counts itself, with the labels the meter shows, in the meter's order.
 */
export const DENSITY_METRICS: readonly { readonly id: string; readonly label: string }[] = [
  { id: "props", label: "Props" },
  { id: "animated", label: "Animated props" },
  { id: "pages", label: "Atlas pages" },
  { id: "tileChunks", label: "Tile chunks" },
];

/**
 * Where a group's entities reach a client (ADR-088 decision 4): `chunks`, with the chunk range the game loads, or
 * `aroundView`, through the server's interest, over the registered camera view plus `marginTiles` on every side,
 * centred on the point and never clamped to the map.
 */
export type DensityReach = "chunks" | { readonly aroundView: { readonly marginTiles: number } };

/**
 * A group a game counts (ADR-088 decision 4): its metric id, its label, the entity types it holds, when given
 * the integer field an entity counts as (a spawn's creature count), and where its entities reach a client,
 * `chunks` when not given.
 */
export interface DensityGroup {
  readonly id: string;
  readonly label: string;
  readonly types: readonly string[];
  readonly countField?: string;
  readonly reach?: DensityReach;
}

/**
 * A game's density registration: its groups, in the meter's order, the budgets by metric id and, when given, the
 * widest view the game loads chunks for (ADR-088 decision 2), the registered camera view when not.
 */
export interface DensityDefinition {
  readonly groups: readonly DensityGroup[];
  readonly budgets: { readonly [metric: string]: DensityBudget };
  readonly view?: { readonly width: number; readonly height: number };
}

/**
 * One metric's reading: its id, label, value, budget (null when the game sets none) and grade (null likewise).
 */
export interface DensityMetricReading {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly budget: DensityBudget | null;
  readonly grade: DensityGrade | null;
}

/**
 * The props in range drawn from one atlas page, `page` null for frames the manifest lacks.
 */
export interface DensityPageReading {
  readonly page: number | null;
  readonly props: number;
}

/**
 * A rectangle in world pixels.
 */
export interface DensityRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The rectangle a group reaching around the view is counted over.
 */
export interface DensityReachRect {
  readonly id: string;
  readonly rect: DensityRect;
}

/**
 * A whole reading: the point, the view placed there, the chunk range and its pixel rectangle (no larger than the
 * map), the metrics in the meter's order, the props per page, by page with the unknown page last, and the
 * rectangle of each group that reaches around the view, in the groups' order.
 */
export interface DensityReading {
  readonly centre: DensityPoint;
  readonly view: ViewRect;
  readonly range: ChunkRange;
  readonly rect: DensityRect;
  readonly metrics: readonly DensityMetricReading[];
  readonly pages: readonly DensityPageReading[];
  readonly reaches: readonly DensityReachRect[];
}

/**
 * What a measurement reads. `map` is the map with its followed cells lowered (`lowerCellSources`), so an area
 * that follows a zone counts as its cells; `view` is the widest view the game loads chunks for and `cameraView`
 * the registered camera view a group reaching around the view is measured from; `cells` gives a tile or terrain
 * layer's resolved cells, null for a layer without them.
 */
export interface DensityInput {
  readonly map: MapDocument;
  readonly tileSize: number;
  readonly chunkSize: number;
  readonly view: { readonly width: number; readonly height: number };
  readonly cameraView: { readonly width: number; readonly height: number };
  readonly centre: DensityPoint;
  readonly manifest: AssetManifest;
  readonly cells: (layerId: string) => ArrayLike<number> | null;
  readonly entityTypes: readonly EntityTypeDefinition[];
  readonly definition: DensityDefinition;
}

/**
 * Clamps a view's near edge on one axis as Phaser's bounded camera does: inside `[0, max(0, map - view)]`, so a
 * map smaller than the view holds the edge at 0.
 *
 * @param centre The point on the axis.
 * @param view The view's size on the axis.
 * @param map The map's size on the axis.
 * @returns The near edge.
 */
function clampEdge(centre: number, view: number, map: number): number {
  return Math.min(Math.max(centre - view / 2, 0), Math.max(0, map - view));
}

/**
 * The view a camera of the given size shows when centred on a point of a map it is bounded by.
 *
 * @param centre The point in world pixels.
 * @param view The camera's view size in world pixels.
 * @param map The map's size in world pixels.
 * @returns The view rectangle.
 */
export function densityView(centre: DensityPoint, view: { readonly width: number; readonly height: number }, map: { readonly width: number; readonly height: number }): ViewRect {
  return { left: clampEdge(centre.x, view.width, map.width), top: clampEdge(centre.y, view.height, map.height), width: view.width, height: view.height };
}

/**
 * A value's grade against a budget.
 *
 * @param value The count.
 * @param budget The budget, or null.
 * @returns Green under amber, amber from amber to under red, red from red; null without a budget.
 */
export function gradeOf(value: number, budget: DensityBudget | null): DensityGrade | null {
  if (budget === null) {
    return null;
  }
  return value >= budget.red ? "red" : value >= budget.amber ? "amber" : "green";
}

const GRADE_ORDER: readonly DensityGrade[] = ["green", "amber", "red"];

/**
 * The worst grade among readings, which colours the measured range on the map.
 *
 * @param metrics The readings.
 * @returns Red over amber over green; null when none is graded.
 */
export function worstGrade(metrics: readonly DensityMetricReading[]): DensityGrade | null {
  let worst: DensityGrade | null = null;
  for (const metric of metrics) {
    if (metric.grade !== null && (worst === null || GRADE_ORDER.indexOf(metric.grade) > GRADE_ORDER.indexOf(worst))) {
      worst = metric.grade;
    }
  }
  return worst;
}

/**
 * Whether a chunk lies in a range.
 *
 * @param range The range.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @returns True when it does.
 */
function inRange(range: ChunkRange, cx: number, cy: number): boolean {
  return cx >= range.minCx && cx <= range.maxCx && cy >= range.minCy && cy <= range.maxCy;
}

/**
 * The props in range: the images they draw, one a prop (ADR-132 decision 1), the looping animated ones, and the
 * count per page.
 *
 * @param input The input.
 * @param range The range.
 * @returns The counts.
 */
function countProps(input: DensityInput, range: ChunkRange): { readonly props: number; readonly animated: number; readonly pages: readonly DensityPageReading[] } {
  const { map, chunkSize, manifest } = input;
  let props = 0;
  let animated = 0;
  const perPage = new Map<number | null, number>();
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (!inRange(range, Math.floor(prop.x / chunkSize), Math.floor(prop.y / chunkSize))) {
        continue;
      }
      const id = frameId(prop.frame.tileset, null, prop.frame.tile);
      const rect = manifest.frames[id];
      props += 1;
      const animation = manifest.animations[id];
      if (animation !== undefined && animation.once !== true) {
        animated += 1;
      }
      const page = rect?.page ?? null;
      perPage.set(page, (perPage.get(page) ?? 0) + 1);
    }
  }
  const pages = [...perPage].map(([page, count]) => ({ page, props: count })).sort((a, b) => (a.page ?? Number.MAX_SAFE_INTEGER) - (b.page ?? Number.MAX_SAFE_INTEGER));
  return { props, animated, pages };
}

/**
 * Whether a layer's cells hold a painted cell inside one chunk.
 *
 * @param cells The layer's resolved cells, row-major over the map.
 * @param width The map's width in tiles.
 * @param height The map's height in tiles.
 * @param chunkSize Chunk size in tiles.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @returns True when one cell is not empty.
 */
function chunkPainted(cells: ArrayLike<number>, width: number, height: number, chunkSize: number, cx: number, cy: number): boolean {
  const x1 = Math.min(width, (cx + 1) * chunkSize);
  const y1 = Math.min(height, (cy + 1) * chunkSize);
  for (let y = cy * chunkSize; y < y1; y += 1) {
    for (let i = y * width + cx * chunkSize, end = y * width + x1; i < end; i += 1) {
      const cell = cells[i];
      if (cell !== undefined && cell !== EMPTY_TILE) {
        return true;
      }
    }
  }
  return false;
}

/**
 * The pairs of a tile or terrain layer and a chunk of the range in which it paints a cell.
 *
 * @param input The input.
 * @param range The range.
 * @returns The count.
 */
function countTileChunks(input: DensityInput, range: ChunkRange): number {
  const { map, chunkSize } = input;
  let count = 0;
  for (const layer of map.layers) {
    if (layer.kind !== "tile" && layer.kind !== "terrain") {
      continue;
    }
    const cells = input.cells(layer.id);
    if (cells === null) {
      continue;
    }
    for (let cy = range.minCy; cy <= range.maxCy; cy += 1) {
      for (let cx = range.minCx; cx <= range.maxCx; cx += 1) {
        if (chunkPainted(cells, map.width, map.height, chunkSize, cx, cy)) {
          count += 1;
        }
      }
    }
  }
  return count;
}

/**
 * What one entity counts as: its count field's value when that is a whole number of 0 or more, else the
 * field's schema default when that is one, else 1.
 *
 * @param fields The entity's fields.
 * @param definition Its type's definition.
 * @param countField The group's count field, if any.
 * @returns The weight.
 */
function weightOf(fields: { readonly [key: string]: JsonValue }, definition: EntityTypeDefinition | undefined, countField: string | undefined): number {
  if (countField === undefined) {
    return 1;
  }
  const value = fields[countField];
  if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
    return value;
  }
  const property = definition?.schema.properties[countField];
  const fallback = property !== undefined && "default" in property ? property.default : undefined;
  return typeof fallback === "number" && Number.isInteger(fallback) && fallback >= 0 ? fallback : 1;
}

/**
 * The rectangle a group reaching around the view is counted over: the camera view plus the margin on every side,
 * centred on the point, not clamped.
 *
 * @param input The input.
 * @param marginTiles The margin in tiles.
 * @returns The rectangle in world pixels.
 */
function aroundViewRect(input: DensityInput, marginTiles: number): DensityRect {
  const width = input.cameraView.width + 2 * marginTiles * input.tileSize;
  const height = input.cameraView.height + 2 * marginTiles * input.tileSize;
  return { x: input.centre.x - width / 2, y: input.centre.y - height / 2, width, height };
}

/**
 * Whether an entity is in its group's area. Over the chunk range, the chunk holding its point is in range; around
 * the view, its point lies in the rectangle, edges included. Either way, a cell of one of its areas that overlaps
 * the area's rectangle counts too (an edge shared is not an overlap).
 *
 * @param entity The entity.
 * @param definition Its type's definition, which names its cells fields.
 * @param input The input.
 * @param range The chunk range, or null when the group reaches around the view.
 * @param rect The area in world pixels: the range's, or the rectangle around the view.
 * @returns True when it is.
 */
function entityInRange(entity: EntityInstance, definition: EntityTypeDefinition | undefined, input: DensityInput, range: ChunkRange | null, rect: DensityRect): boolean {
  const { chunkSize, tileSize } = input;
  if (range !== null && inRange(range, Math.floor(Math.floor(entity.x) / chunkSize), Math.floor(Math.floor(entity.y) / chunkSize))) {
    return true;
  }
  if (range === null) {
    const x = entity.x * tileSize;
    const y = entity.y * tileSize;
    if (x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height) {
      return true;
    }
  }
  if (definition === undefined) {
    return false;
  }
  const anchor = thingAnchor(entity, tileSize);
  const cellPixels = tileSize / COLLISION_CELLS_PER_TILE;
  for (const area of cellAreasOf(definition.schema, entity.fields)) {
    for (const cell of area.cells) {
      const box = cellBox(anchor, cell, cellPixels);
      if (box.x < rect.x + rect.width && box.x + box.w > rect.x && box.y < rect.y + rect.height && box.y + box.h > rect.y) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Each group's count over the range.
 *
 * @param input The input.
 * @param range The range.
 * @param rect The range in world pixels.
 * @param reaches The rectangle of each group that reaches around the view, by group id.
 * @returns The counts by group id.
 */
function countGroups(input: DensityInput, range: ChunkRange, rect: DensityRect, reaches: ReadonlyMap<string, DensityRect>): Map<string, number> {
  const counts = new Map<string, number>(input.definition.groups.map((group) => [group.id, 0]));
  const groupOf = new Map<string, DensityGroup>();
  for (const group of input.definition.groups) {
    for (const type of group.types) {
      groupOf.set(type, group);
    }
  }
  const definitions = new Map(input.entityTypes.map((definition) => [definition.type, definition]));
  for (const layer of input.map.layers) {
    if (layer.kind !== "entity") {
      continue;
    }
    for (const entity of layer.entities) {
      const group = groupOf.get(entity.type);
      if (group === undefined) {
        continue;
      }
      const definition = definitions.get(entity.type);
      const around = reaches.get(group.id);
      if (around === undefined ? entityInRange(entity, definition, input, range, rect) : entityInRange(entity, definition, input, null, around)) {
        counts.set(group.id, (counts.get(group.id) ?? 0) + weightOf(entity.fields, definition, group.countField));
      }
    }
  }
  return counts;
}

/**
 * Measures what the game loads around a point.
 *
 * @param input What to measure and with what.
 * @returns The reading.
 */
export function measureDensity(input: DensityInput): DensityReading {
  const { map, tileSize, chunkSize, definition } = input;
  const view = densityView(input.centre, input.view, { width: map.width * tileSize, height: map.height * tileSize });
  const range = visibleChunks(view, tileSize, chunkSize, Math.ceil(map.width / chunkSize), Math.ceil(map.height / chunkSize));
  const chunkPixels = chunkSize * tileSize;
  const x = range.minCx * chunkPixels;
  const y = range.minCy * chunkPixels;
  const rect = { x, y, width: Math.min((range.maxCx + 1) * chunkPixels, map.width * tileSize) - x, height: Math.min((range.maxCy + 1) * chunkPixels, map.height * tileSize) - y };
  const reaches = definition.groups.flatMap((group): DensityReachRect[] => (group.reach === undefined || group.reach === "chunks" ? [] : [{ id: group.id, rect: aroundViewRect(input, group.reach.aroundView.marginTiles) }]));
  const props = countProps(input, range);
  const groups = countGroups(input, range, rect, new Map(reaches.map((reach) => [reach.id, reach.rect])));
  const values: Readonly<Record<string, number>> = { props: props.props, animated: props.animated, pages: props.pages.filter((page) => page.page !== null).length, tileChunks: countTileChunks(input, range) };
  const metric = (id: string, label: string, value: number): DensityMetricReading => {
    const budget = definition.budgets[id] ?? null;
    return { id, label, value, budget, grade: gradeOf(value, budget) };
  };
  const metrics = [...DENSITY_METRICS.map((m) => metric(m.id, m.label, values[m.id] ?? 0)), ...definition.groups.map((group) => metric(group.id, group.label, groups.get(group.id) ?? 0))];
  return { centre: input.centre, view, range, rect, metrics, pages: props.pages, reaches };
}
